import React, {FC, useCallback} from 'react'
import {useSelector} from 'react-redux'
import {useTrackOfferView} from 'hooks/analytics'
import {useOfferClickHandler} from 'hooks/useOfferClickHandler'
import {getMetaBrandHasRooms} from 'modules/accommodation/selectors'
import {useSapi} from 'modules/sapiSearch/SapiProvider'
import {getOfferAccessFlowSettings} from 'modules/sapiSearch/selectors'
import {getHotelApiRoomsParameters} from 'modules/searchApi/selectors'

import getIsBofhOffer from '@vio/core/src/offer/business/isBofhOffer'
import {getOfferHelperClasses} from '@vio/core/src/offer/business/privateDeals'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'

import {LockedDeal} from './LockedDeal'
import {OfferLink} from './OfferLink'

interface Props {
  anchorPriceTotal: number
  component: string
  hotelId: string
  offer: SearchOffer
  offerPosition: number
  onClick?: (offer: SearchOffer) => void
  isDiscount?: boolean
  isSplitBooking?: boolean
  hasInteraction?: boolean
}

const preHeatedOffers: Record<string, boolean> = {}

/**
 * A wrapper component that handles all interactions on offers
 * Avoid adding styling here, it should transparently add interactivity
 */
export const OfferInteraction: FC<Props> = ({
  children,
  anchorPriceTotal,
  component,
  hotelId,
  offer,
  offerPosition,
  onClick,
  isDiscount,
  isSplitBooking,
  hasInteraction = true
}) => {
  const {sapiClient} = useSapi()

  const {handleClick, handleAlternativeClick} = useOfferClickHandler({
    component,
    hotelId,
    offer,
    offerPosition,
    onClick,
    isDiscount,
    isSplitBooking
  })

  const isBofhOffer = getIsBofhOffer(offer)
  const brandHasRooms = useSelector(getMetaBrandHasRooms)

  const roomsEndpointParams = useSelector(state =>
    getHotelApiRoomsParameters(state, hotelId)
  )
  const preHeatRoomOffers = useCallback(() => {
    // we are expecting to have sapiClient while running the app but
    // it seems like storybook run the tests while not having access to sapiClient
    // at early stages so this condition has been added to prevent any errors on storybook
    // Skip prefetching for Search Whitelabels (brands that don't support rooms)
    if (
      isBofhOffer &&
      brandHasRooms &&
      !preHeatedOffers[offer?.id] &&
      sapiClient
    ) {
      sapiClient.rooms({
        ...roomsEndpointParams,
        providerCode: offer.providerCode,
        clickedOfferId: offer.id,
        preHeat: 1
      })
      preHeatedOffers[offer?.id] = true
    }
  }, [isBofhOffer, brandHasRooms, sapiClient, roomsEndpointParams, offer])

  const inViewRef = useTrackOfferView(offer, hotelId, component)

  const {isAuthLocked} = useSelector(state =>
    getOfferAccessFlowSettings(state, hotelId, offer?.id)
  )

  if (!hasInteraction) return <>{children}</>

  if (isAuthLocked) {
    const dataId = isBofhOffer ? 'BofhOffer--locked' : 'ProviderOffer--locked'

    return (
      <LockedDeal
        inViewRef={inViewRef}
        role="button"
        dataId={dataId}
        onClick={onClick}
        component={component}
        hotelId={hotelId}
        offer={offer}
        offerPosition={offerPosition}
        isDiscount={isDiscount}
        isSplitBooking={isSplitBooking}
      >
        {children}
      </LockedDeal>
    )
  }

  const dataId = isBofhOffer ? 'BofhOffer' : 'ProviderOffer'
  const addNoReferrer = isBofhOffer ? '' : 'noreferrer'

  return (
    <OfferLink
      inViewRef={inViewRef}
      className={getOfferHelperClasses(offer)}
      addNoReferrer={addNoReferrer}
      dataId={dataId}
      onHover={preHeatRoomOffers}
      handleClick={handleClick}
      anchorPriceTotal={anchorPriceTotal}
      offer={offer}
      hotelId={hotelId}
      onContextMenu={handleAlternativeClick}
      onAuxClick={handleAlternativeClick}
    >
      {children}
    </OfferLink>
  )
}
